import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

const featuredPost = {
  title: 'How AI Receptionists Work: A Complete Guide',
  excerpt:
    'From voice recognition to appointment booking — discover the technology that powers your 24/7 virtual front desk. Learn how speech recognition, natural language processing, and calendar integration come together.',
  category: 'Product Guide',
  date: 'Feb 20, 2025',
  readTime: '10 min read',
  image:
    'https://public.readdy.ai/ai/img_res/46b10bf8f036b46723b6e2d9f5420f1d.jpg',
  slug: '/blog/how-ai-receptionists-work',
};

const posts = [
  {
    title: '5 Signs Your Business Needs an AI Receptionist',
    excerpt:
      "Missing calls, overwhelmed staff, and lost revenue are just the beginning. Here are the top indicators it's time to automate.",
    category: 'Business Tips',
    date: 'Jan 10, 2025',
    readTime: '5 min read',
    image:
      'https://readdy.ai/api/search-image?query=Business%20professional%20looking%20stressed%20at%20desk%20with%20multiple%20phone%20lines%20ringing%2C%20modern%20office%20setting%20warm%20lighting%20clean%20composition%20professional%20photography%20style&width=600&height=400&seq=blog-post-001&orientation=landscape',
    slug: '#',
  },
  {
    title: 'The Complete Guide to AI Appointment Scheduling',
    excerpt:
      'Learn how AI-powered scheduling eliminates double bookings, reduces no-shows, and keeps your calendar running smoothly.',
    category: 'Product Guide',
    date: 'Jan 5, 2025',
    readTime: '6 min read',
    image:
      'https://readdy.ai/api/search-image?query=Digital%20calendar%20interface%20with%20smart%20scheduling%20features%2C%20clean%20modern%20UI%20design%2C%20appointment%20slots%20highlighted%2C%20professional%20business%20tool%20visualization%2C%20soft%20warm%20tones&width=600&height=400&seq=blog-post-002&orientation=landscape',
    slug: '#',
  },
  {
    title: 'Multi-Language Support: Serving Global Customers Effortlessly',
    excerpt:
      'How our AI handles conversations in 30+ languages and why multilingual support is critical for modern businesses.',
    category: 'Features',
    date: 'Dec 28, 2024',
    readTime: '4 min read',
    image:
      'https://readdy.ai/api/search-image?query=World%20map%20with%20connected%20communication%20nodes%20and%20speech%20bubbles%20in%20different%20languages%2C%20modern%20flat%20design%20warm%20color%20palette%20with%20coral%20and%20teal%20accents%20global%20connectivity%20concept&width=600&height=400&seq=blog-post-003&orientation=landscape',
    slug: '#',
  },
  {
    title: "Case Study: How a Dental Practice Increased Bookings by 60%",
    excerpt:
      "Dr. Sarah Kim shares how switching to AI Receptionist transformed her practice's patient experience and bottom line.",
    category: 'Case Study',
    date: 'Dec 20, 2024',
    readTime: '7 min read',
    image:
      'https://readdy.ai/api/search-image?query=Modern%20dental%20office%20reception%20area%20with%20friendly%20atmosphere%2C%20clean%20white%20interior%2C%20professional%20healthcare%20environment%2C%20warm%20welcoming%20lighting%2C%20contemporary%20design&width=600&height=400&seq=blog-post-004&orientation=landscape',
    slug: '#',
  },
  {
    title: 'AI vs. Traditional Answering Services: A Cost Comparison',
    excerpt:
      'We break down the real costs of human answering services versus AI receptionists — the numbers might surprise you.',
    category: 'Business Tips',
    date: 'Dec 15, 2024',
    readTime: '5 min read',
    image:
      'https://readdy.ai/api/search-image?query=Split%20comparison%20infographic%20showing%20traditional%20phone%20operator%20versus%20modern%20AI%20technology%2C%20clean%20design%2C%20professional%20business%20concept%20warm%20neutral%20tones%20with%20accent%20colors&width=600&height=400&seq=blog-post-005&orientation=landscape',
    slug: '#',
  },
  {
    title: 'Setting Up Your AI Receptionist in Under 10 Minutes',
    excerpt:
      'A step-by-step walkthrough of our onboarding process — from sign-up to your first AI-answered call.',
    category: 'Product Guide',
    date: 'Dec 10, 2024',
    readTime: '4 min read',
    image:
      'https://readdy.ai/api/search-image?query=Person%20setting%20up%20software%20on%20laptop%20with%20simple%20step%20by%20step%20interface%2C%20clean%20modern%20workspace%20warm%20ambient%20lighting%20productivity%20concept%20minimalist%20design&width=600&height=400&seq=blog-post-006&orientation=landscape',
    slug: '#',
  },
];

const categories = [
  'All',
  'Industry Trends',
  'Business Tips',
  'Product Guide',
  'Features',
  'Case Study',
];

export default function BlogPage() {
  const [scrolled, setScrolled] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');

  // Scroll listener for navbar background/shadow
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredPosts =
    activeCategory === 'All'
      ? posts
      : posts.filter((p) => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center cursor-pointer">
            <img
              src="https://static.readdy.ai/image/e31f2f4702878072217cd119276d8644/5b56961b60c36d4ff2ff86c82d13a1e4.png"
              alt="AI Receptionist Logo"
              className="h-12 w-auto"
            />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              Home
            </Link>
            <Link
              to="/about"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              About
            </Link>
            <Link
              to="/demo"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              Demo
            </Link>
            <Link
              to="/contact"
              className="text-sm font-medium text-gray-700 hover:text-purple-600 transition-colors cursor-pointer"
            >
              Contact
            </Link>
            <a
              href="/#contact"
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Get Started
            </a>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-bold tracking-widest text-purple-600 uppercase mb-4">
            OUR BLOG
          </p>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
            Insights &amp; Resources
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl">
            Tips, guides, and stories to help you get the most out of AI-powered
            business communications.
          </p>
        </div>
      </section>

      {/* Featured Post */}
      <section className="pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          <Link
            to={featuredPost.slug}
            className="group block bg-gray-50 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
          >
            <div className="grid md:grid-cols-2">
              <div className="w-full h-64 md:h-96 overflow-hidden">
                <img
                  src={featuredPost.image}
                  alt={featuredPost.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 bg-purple-100 text-purple-600 text-xs font-bold rounded-full">
                    {featuredPost.category}
                  </span>
                  <span className="text-xs text-gray-500">{featuredPost.date}</span>
                  <span className="text-xs text-gray-500">
                    • {featuredPost.readTime}
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 group-hover:text-purple-600 transition-colors leading-tight">
                  {featuredPost.title}
                </h2>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {featuredPost.excerpt}
                </p>
                <div className="flex items-center gap-2 text-purple-600 font-semibold text-sm">
                  Read Article <i className="ri-arrow-right-line"></i>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Category Filter */}
      <section className="pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, i) => (
              <a
                key={i}
                href={post.slug}
                className="group block bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
              >
                <div className="w-full h-52 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-3 py-1 bg-purple-50 text-purple-600 text-xs font-bold rounded-full">
                      {post.category}
                    </span>
                    <span className="text-xs text-gray-400">{post.readTime}</span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors leading-snug">
                    {post.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{post.date}</span>
                    <span className="text-purple-600 text-sm font-medium flex items-center gap-1">
                      Read <i className="ri-arrow-right-s-line"></i>
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-article-line text-5xl text-gray-300 mb-4"></i>
              <p className="text-gray-500">
                No posts found in this category yet.
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
